import React, { useState } from 'react';
import { EraserIcon } from 'lucide-react';
import { useAssistantChatStore } from '../../store/assistantChatStore';
import Tooltip from '../common/Tooltip';

interface ChatViewClearButtonProps {
  className?: string;
}

const ChatViewClearButton: React.FC<ChatViewClearButtonProps> = ({ className = '' }) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [isClearing, setIsClearing] = useState(false);

  const activeSessionKey = useAssistantChatStore((state) => state.activeSessionKey);
  const messages = useAssistantChatStore((state) => {
    const key = state.activeSessionKey;
    if (!key) return [];
    return state.messagesBySession[key] || [];
  });
  const clearMessages = useAssistantChatStore((state) => state.clearMessages);
  const isLoading = useAssistantChatStore((state) => state.isLoading);

  const hasMessages = messages.length > 0;

  const handleClick = () => {
    if (!hasMessages) return;
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    if (!activeSessionKey) return;

    setIsClearing(true);
    try {
      await clearMessages(activeSessionKey);
      setShowConfirm(false);
    } catch (error) {
      console.error('Failed to clear conversation:', error);
      // Error is already handled in the store
      setShowConfirm(false);
    } finally {
      setIsClearing(false);
    }
  };

  const handleCancel = () => {
    setShowConfirm(false);
  };

  return (
    <>
      <Tooltip content={!hasMessages ? 'No messages to clear' : 'Clear conversation'}>
      <button
        onClick={handleClick}
        disabled={!hasMessages || isLoading || isClearing}
        className={`
          p-2 rounded-md transition-colors
          ${hasMessages && !isLoading && !isClearing
            ? 'text-gray-600 dark:text-gray-400 hover:text-red-600 hover:bg-red-50'
            : 'text-gray-400 cursor-not-allowed'
          }
          ${className}
        `}
        aria-label="Clear conversation"
      >
        {isClearing ? (
          <div className="w-5 h-5 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
        ) : (
          <EraserIcon className="w-5 h-5" />
        )}
      </button>
      </Tooltip>

      {/* Simple confirmation dialog */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div
              className="fixed inset-0 transition-opacity bg-gray-500 dark:bg-black bg-opacity-75"
              onClick={handleCancel}
            />

            {/* Modal panel */}
            <div className="inline-block w-full max-w-sm p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-cap-dark shadow-xl rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 dark:text-warm-white mb-4">
                Clear Conversation?
              </h3>

              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                This will delete {messages.length} message{messages.length !== 1 ? 's' : ''} from the conversation. This action cannot be undone.
              </p>

              <div className="flex items-center justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={isClearing}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isClearing ? 'Clearing...' : 'Clear'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatViewClearButton;
